<template>
  <div>
    <v-col class="pa-0">
      <!-- Name -->
      <div class="homepageSubTitle font-weight-bold px-0 px-md-3">
        {{ selectedProduct.Name }}
      </div>

      <v-row class="ma-0">
        <v-col cols="12" md="6" class="px-0 px-md-3">
          <!-- Pictures -->
          <ProductMedia
            :product="selectedProduct"
            :selectedItem="selectedItem"
            class="mb-3"
          />

          <!-- Buttons to show extra info, like video and preparation instructions -->
          <ProductInfoButtons :product="selectedProduct" />

          <!-- Lead time alert if it's above threshold -->
          <ProductAvailabilityDetails
            :item="selectedProduct"
          ></ProductAvailabilityDetails>

          <!-- Description for non-mobile screen -->
          <v-col
            v-if="description.length > 0"
            class="text-justify pa-0 py-2"
            :class="isMobile ? 'd-none' : ''"
          >
            <div class="text-subtitle-1 font-weight-bold mb-1">
              {{ $translate('DESCRIPTION') }}
            </div>
            <div v-html="description" />
          </v-col>

          <!-- Labels for non-mobile screen -->
          <ProductLabels
            v-if="
              selectedProduct.Labels != null &&
              selectedProduct.Labels.length > 0
            "
            :labels="selectedProduct.Labels"
            class="my-3"
            :class="isMobile ? 'd-none' : ''"
          />
        </v-col>

        <v-col cols="12" md="6" class="px-0 px-md-3 pt-0 pt-md-3">
          <!-- Product benefits if exist -->
          <ProductBenefits
            v-if="benefits.length > 0"
            :benefits="benefits"
            class="mb-4"
          />

          <!-- Product options if exist -->
          <v-col
            class="pa-0"
            v-for="(list, index) in selectedProduct.OptionLists"
            :key="index"
          >
            <ProductOptions
              v-if="
                selectedProduct.OptionLists != null &&
                selectedProduct.OptionLists.length > 0
              "
              :selectedProduct="list"
              class="mb-4"
              @validateOptionSelection="onValidateOptionSelection"
            />
          </v-col>

          <ProductPersonalDiscount :product="selectedProduct" class="mb-8" />

          <!-- Variations selection if available -->
          <v-row
            v-if="getSortedItemDefNames(selectedProduct.ItemDefs).length > 0"
            class="pb-2 ma-0"
          >
            <v-col
              v-for="itemName in getSortedItemDefNames(
                selectedProduct.ItemDefs
              )"
              cols="12"
              md="6"
              class="pb-2 pt-0 px-0 pr-2"
              :key="itemName"
            >
              <v-select
                v-model="selectedItemDefs[itemName]"
                :items="getSortedItemDefValues(selectedProduct, itemName)"
                :label="itemName"
                hide-details="auto"
                dense
                filled
                @input="onItemDefChange(itemName)"
              />
            </v-col>
          </v-row>

          <!-- Out of stock alert -->
          <v-alert v-model="outOfStock" type="error" dense>
            {{ $translate('OUT_OF_STOCK') }}
          </v-alert>

          <!-- Unit price -->
          <v-row v-if="showPrice" class="ma-0">
            <div>{{ $translate('UNIT_PRICE') }}:</div>
            <v-spacer />
            <div class="text-right text--primary">
              <span
                v-if="showRetialPrice"
                class="discount-price text-body-2 mr-1"
              >
                {{ formatter.format(retailUnitPrice) }}
              </span>
              <span
                class="text-subtitle-1"
                :class="hasGeneralDiscount ? 'red--text' : ''"
              >
                {{ formatter.format(unitPrice) }}
              </span>
              <span v-if="isWeightedProduct" class="text-caption">
                /
                <span v-if="selectedProduct.WeightedInfo.OverviewSize > 1">
                  {{ selectedProduct.WeightedInfo.OverviewSize }}
                </span>
                {{ selectedProduct.WeightedInfo.Unit }}
              </span>
              <span class="text-caption text--secondary">
                {{ priceSuffix }}
              </span>
            </div>
          </v-row>

          <!-- Quantity operations -->
          <v-row class="ma-0 py-2" align="center">
            <div>{{ $translate('AMOUNT_OF_PRODUCTS') }}:</div>

            <v-spacer />

            <v-btn-toggle rounded>
              <v-btn
                small
                :disabled="decreaseBtnDisabled"
                @click="decreaseQuantity"
              >
                <v-icon small>ti-minus</v-icon>
              </v-btn>
              <v-card
                height="28"
                min-width="60"
                flat
                outlined
                tile
                class="text-center px-3 text-subtitle-2 font-weight-medium d-flex"
              >
                <div class="mx-auto">
                  {{ getDisplayQuantity(selectedProduct, quantity) }}
                </div>
                <span v-if="isWeightedProduct" class="ml-1">
                  {{ getDisplayUnit(selectedProduct) }}
                </span>
              </v-card>
              <v-btn
                small
                :disabled="increaseBtnDisabled"
                @click="increaseQuantity"
              >
                <v-icon small>ti-plus</v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-row>

          <v-row v-if="selectedProduct.DepositValue > 0" class="ma-0 py-2">
            <div class="ma-0">
              {{ $translate('DEPOSIT_VALUE') }}:
            </div>
            <v-spacer />
            <div  class="text--primary text-subtitle-1">
              <span>{{ formatter.format(selectedProduct.DepositValue * quantity) }}</span>
            </div>
          </v-row>

          <!-- Total price -->
          <v-row v-if="showPrice" class="ma-0 py-2">
            <div class="font-weight-bold">
              {{ totalPriceTitle }}
            </div>

            <v-spacer />

            <div class="text-right">
              <v-col class="pa-0">
                <div>
                  <span v-if="showRetialPrice" class="discount-price mr-1">
                    {{ formatter.format(totalRetailPrice) }}
                  </span>
                  <span
                    class="text-h5 font-weight-bold"
                    :class="hasGeneralDiscount ? 'red--text' : ''"
                    >{{ formatter.format(totalPrice) }}</span
                  >
                  <span class="text-caption text--secondary">
                    {{ priceSuffix }}
                  </span>
                </div>
                <div v-if="isProfUser">
                  <span class="text-subtitle-2">
                    {{ formatter.format(totalPriceInclVat) }}
                  </span>
                  <span class="text-caption text--secondary">
                    {{ getVATText(selectedProduct.VatPercentage) }}
                  </span>
                </div>
              </v-col>
            </div>
          </v-row>

          <v-row
            v-if="isWeightedProduct && !selectedProduct.WeightedInfo.FixedPrice"
            class="text-left ma-0 py-2"
            style="font-size: 12px"
          >
            <i>{{ $translate('PRODUCT_WEIGHTED_ATTENTION_TEXT') }}</i>
          </v-row>

          <!-- Lead time alert if it's above threshold -->
          <v-alert
            v-if="showLeadTimeAlert"
            color="blue"
            dense
            text
            type="info"
            class="mt-3"
          >
            {{ $translate('PRODUCT_LEAD_TIME_ALERT') }}
            <span class="font-weight-bold">{{ leadTimeStr }}</span>
          </v-alert>

          <!-- Date and time alert if a product has time  -->
          <!-- <v-alert
            v-if="ShowDateTime"
            color="blue"
            type="info"
            dense
            text
            class="mt-3"
          >
            {{ $translate('PRODUCT_DATE_TIME_ALERT') }}
            <span style="font-weight: bold">
              {{ this.selectedProduct.ReleaseDate }}
              {{ this.selectedProduct.ReleaseTime }}
              <span v-if="Datetime">
                {{ $translate('PRODUCT_DATE_TIME') }}</span
              >
              {{ this.selectedProduct.ExpirationDate }}
              {{ this.selectedProduct.ExpirationTime }}
            </span>
          </v-alert> -->

          <!-- List selection and book button -->
          <SingleProductOrderAddToList
            :selectedProduct="selectedProduct"
            :selectedItem="selectedItem"
            :quantity="quantity"
            :backgroundColor="orderBtnBgColor"
            class="mt-3 add-to-list"
            @book="onBookBtnClick"
            @login="goToLogin"
          />
        </v-col>
      </v-row>

      <v-row class="ma-0">
        <v-col class="px-0 px-md-3">
          <!-- Description for mobile screen -->
          <v-col
            v-if="description.length > 0"
            class="text-justify pa-0 py-2"
            :class="isMobile ? '' : 'd-none'"
          >
            <div class="text-subtitle-1 font-weight-bold mb-1">
              {{ $translate('DESCRIPTION') }}
            </div>
            <div v-html="description" />
          </v-col>

          <!-- Labels for mobile screen -->
          <ProductLabels
            v-if="
              selectedProduct.Labels != null &&
              selectedProduct.Labels.length > 0
            "
            :labels="selectedProduct.Labels"
            class="mx-1 my-3"
            :class="isMobile ? '' : 'd-none'"
          />

          <!-- Suggestions -->
          <div v-if="suggestions.length > 0" class="my-5">
            <h2>{{ $translate('SUGGESTIONS') }}</h2>
            <Suggestions :suggestions="suggestions" />
          </div>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<script>
import uiHelpers from '@/shared/ui_helpers';
import utility from '@/shared/utility';
import {
  getSortedItemDefNames,
  getSortedItemDefValues,
  removeDefaultItem,
  getTotalRetailPriceInclVAT,
  getTotalRetailPriceExclVAT,
  getTotalPriceInclVAT,
  getTotalPriceExclVAT,
  getVATText,
  getUnitPrice,
  getRetailUnitPrice,
  getOverviewPrice,
  getRetailOverviewPrice,
  quantityDecreasable,
  quantityIncreasable,
  getQuantity,
  getDisplayQuantity,
  getDisplayUnit,
  hasCapacity,
} from '@/objects/Product';
import ProductMedia from '@/components/event/productDetails/ProductMedia';
import ProductInfoButtons from '@/components/event/productDetails/ProductInfoButtons';
import ProductBenefits from '@/components/event/productDetails/ProductBenefits';
import ProductLabels from '@/components/event/productDetails/ProductLabels';
import ProductOptions from '@/components/event/productDetails/ProductOptions';
import ProductPersonalDiscount from '@/components/event/productDetails/ProductPersonalDiscount';
import Suggestions from '@/components/generalComponents/Suggestions';
import SingleProductOrderAddToList from '@/components/event/eventDetails/productOrderComponents/SingleProductOrderAddToList';
import ProductAvailabilityDetails from '@/components/event/eventDetails/productOrderComponents/ProductAvailabilityDetails';

const SHOPPING_LIST_FLOW = 'shoppingListFlow';
const SHOPPING_CART_FLOW = 'shoppingCartFlow';

export default {
  mixins: [uiHelpers],
  name: 'SingleProductOrder',
  components: {
    ProductMedia,
    ProductInfoButtons,
    ProductBenefits,
    ProductOptions,
    ProductLabels,
    SingleProductOrderAddToList,
    Suggestions,
    ProductPersonalDiscount,
    ProductAvailabilityDetails,
  },
  props: {
    selectedProduct: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      selectedShoppingList: {},
      selectedFlow: '',
      forAgeAbove17: false,
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      quantity: 1,
      rentalData: {},
      readMoreEnabled: false,
      selectedItem: {},
      selectedItemDefs: {},

      // Order flow
      shoppingListFlow: SHOPPING_LIST_FLOW,
      shoppingCartFlow: SHOPPING_CART_FLOW,

      invalidOptionSelection: false,
    };
  },
  watch: {
    selectedProduct() {
      this.init();
    },
  },
  computed: {
    userLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
    lowestPrice() {
      if (
        this.selectedProduct == null ||
        this.selectedProduct.Items == null ||
        this.selectedProduct.Items.length === 0
      ) {
        return null;
      }

      let minPrice = Number.MAX_VALUE;

      this.selectedProduct.Items.forEach((item) => {
        if (item.Price < minPrice) {
          minPrice = item.Price;
        }
      });

      return minPrice;
    },
    isWeightedProduct() {
      if (
        this.selectedProduct != null &&
        this.selectedProduct.Type === 'Weighted'
      ) {
        return true;
      }

      return false;
    },
    totalPriceTitle() {
      if (
        this.selectedItem != null ||
        (this.isWeightedProduct && this.selectedProduct.WeightedInfo.FixedPrice)
      ) {
        return this.$translate('TOTAL_PRICE');
      }
      return `${this.$translate('PRICE_STARTING_AT')}:`;
    },
    increaseBtnDisabled() {
      return !quantityIncreasable(
        this.selectedProduct,
        this.selectedItem,
        this.quantity
      );
    },
    decreaseBtnDisabled() {
      return !quantityDecreasable(
        this.selectedProduct,
        this.selectedItem,
        this.quantity
      );
    },
    benefits() {
      const benefitsArray = [];

      if (this.selectedProduct == null) {
        return benefitsArray;
      }

      for (let suffix = 1; suffix <= 5; suffix++) {
        const benefitKey = `Benefit${suffix}`;
        if (
          this.selectedProduct[benefitKey] != null &&
          this.selectedProduct[benefitKey].trim() !== ''
        ) {
          benefitsArray.push(this.selectedProduct[benefitKey]);
        }
      }

      return benefitsArray;
    },
    showLeadTimeAlert() {
      // Show lead time alert only if the lead time of product is above the threshold
      if (
        this.selectedProduct == null ||
        this.selectedProduct.LeadTime == null
      ) {
        return false;
      }

      let leadTimeWarningThreshold = 0;

      const pgInfo = this.$store.getters['Account/partnerGroupDetails'];
      if (pgInfo != null && pgInfo.LeadTimeWarningThreshold != null) {
        leadTimeWarningThreshold = pgInfo.LeadTimeWarningThreshold;
      }

      if (this.selectedProduct.LeadTime > leadTimeWarningThreshold) {
        return true;
      }

      return false;
    },
    Datetime() {
      if (
        this.selectedProduct.ReleaseDate !== '' &&
        this.selectedProduct.ExpirationDate !== ''
      ) {
        return true;
      } else if (
        this.selectedProduct.ReleaseDate === '' &&
        this.selectedProduct.ExpirationDate === ''
      ) {
        return false;
      }
    },
    ShowDateTime() {
      if (
        this.selectedProduct.ReleaseDate !== '' ||
        this.selectedProduct.ExpirationDate !== ''
      ) {
        return true;
      } else if (
        this.selectedProduct.ReleaseDate === '' ||
        this.selectedProduct.ExpirationDate === ''
      ) {
        return false;
      }
    },
    leadTimeStr() {
      if (!this.showLeadTimeAlert) {
        return '';
      }

      const dayInSeconds = 86400;
      const lessThanAnHour = 3600;

      if (this.selectedProduct.LeadTime > dayInSeconds) {
        // Larger than one day. Use [days] as unit
        const leadTimeDays = Math.round(
          this.selectedProduct.LeadTime / dayInSeconds
        );
        return `${leadTimeDays} ${this.$translate('DAYS')}`;
      }

      if (this.selectedProduct.LeadTime < lessThanAnHour) {
        const time =  Math.floor(this.selectedProduct.LeadTime / 60);
        const leadTimeMinutes = time % 60
          return `${leadTimeMinutes} ${this.$translate('MINUTES')}`
      }

      // Smaller than one day. Use [hours] as unit
      const leadTimeHours = Math.round(this.selectedProduct.LeadTime / 3600);
      return `${leadTimeHours} ${this.$translate('HOURS')}`;
    },
    suggestions() {
      if (
        this.selectedProduct == null ||
        this.selectedProduct.Suggestions == null
      ) {
        return [];
      }

      return this.selectedProduct.Suggestions;
    },
    isMobile() {
      if (this.$vuetify.breakpoint.smAndDown) {
        return true;
      }
      return false;
    },
    outOfStock() {
      return !hasCapacity(this.selectedProduct, this.selectedItem);
    },
    orderBtnBgColor() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return 'transparent';
      }
      return '';
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    onDiscount() {
      // General discount check
      if (this.hasGeneralDiscount) {
        return true;
      }

      // Personal discount check
      if (this.personalDiscount > 0) {
        return true;
      }
      return false;
    },
    hasGeneralDiscount() {
      if (this.selectedItem != null && this.selectedItem.Discount) {
        return true;
      }
      return false;
    },
    personalDiscount() {
      const partnerId = this.selectedProduct.PartnerId;
      const relations = this.partnerUserRelations[partnerId];
      if (
        relations != null &&
        relations.DiscountPercentage != null &&
        relations.DiscountPercentage > 0
      ) {
        return relations.DiscountPercentage;
      }
      return 0;
    },
    applyPersonalDiscount() {
      // Decides whether a personal discount should be used or not
      if (this.personalDiscount > 0 && !this.hasGeneralDiscount) {
        return true;
      }
      return false;
    },
    partnerUserRelations() {
      return this.$store.getters['auth/partnerUserRelations'];
    },
    productSelection() {
      return {
        Product: { ...this.selectedProduct },
        ItemId: this.selectedItem.Id,
        Item: { ...this.selectedItem },
        Quantity: this.quantity,
      };
    },
    retailUnitPrice() {
      if (this.isWeightedProduct) {
        if (this.hasGeneralDiscount) {
          return getRetailOverviewPrice(this.productSelection);
        }
        return getOverviewPrice(this.productSelection);
      }

      if (this.hasGeneralDiscount) {
        return getRetailUnitPrice(this.productSelection);
      }
      return getUnitPrice(this.productSelection);
    },
    unitPrice() {
      if (this.isWeightedProduct) {
        return this.getPriceForPersonalDiscount(
          getOverviewPrice(this.productSelection)
        );
      }

      return this.getPriceForPersonalDiscount(
        getUnitPrice(this.productSelection)
      );
    },
    totalPrice() {
      if (this.applyPersonalDiscount) {
        return this.getTotalPriceWithPersonalDiscount();
      }
      return this.getTotalPriceWithoutPersonalDiscount();
    },
    totalRetailPrice() {
      if (this.applyPersonalDiscount) {
        return this.getTotalRetailPriceWithPersonalDiscount();
      }
      return this.getTotalRetailPriceWithoutPersonalDiscount();
    },
    totalPriceInclVat() {
      return this.getPriceForPersonalDiscount(
        getTotalPriceInclVAT(this.productSelection)
      );
    },
    priceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
    description() {
      // Get the description with whitelines (if exists)
      if (
        this.selectedProduct == null ||
        this.selectedProduct.Description == null
      ) {
        return '';
      }

      return utility.lineBreaksToHtml(this.selectedProduct.Description);
    },
    showPrice() {
      if (this.selectedProduct.Type === 'Rental' && this.unitPrice < 0.005) {
        return false;
      }
      return true;
    },
    showRetialPrice() {
      return (
        this.onDiscount &&
        this.retailUnitPrice != null &&
        this.retailUnitPrice > 0 &&
        this.unitPrice != null &&
        this.retailUnitPrice > this.unitPrice
      );
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      // Remove default item in case of ItemDefs configured
      if (
        this.selectedProduct.ItemDefs != null &&
        Object.keys(this.selectedProduct.ItemDefs).length > 0
      ) {
        removeDefaultItem(this.selectedProduct);
      }

      this.initSelectedItemDefs();

      // Init quantity
      this.quantity = getQuantity(
        this.selectedProduct,
        this.selectedItem,
        this.quantity
      );
    },
    initSelectedItemDefs() {
      // Preselect item defs
      this.selectedItemDefs = null;

      if (
        this.selectedProduct.Items != null &&
        this.selectedProduct.Items.length > 0
      ) {
        const cheapestItem = { ...this.selectedProduct.Items[0] };
        this.selectedItem = { ...cheapestItem };
        this.selectedItemDefs = { ...cheapestItem.ItemDef };
      } else {
        this.selectedItem = {};
        this.selectedItemDefs = {};
      }
    },
    goToLogin() {
      window.EventBus.fire('triggerOpenAccountFromMenu', {
        toggle: true,
        component: 'Login',
      });
    },
    getSelectedItem() {
      if (
        this.selectedProduct == null ||
        this.selectedProduct.Items == null ||
        this.selectedProduct.Items.length === 0
      ) {
        // Empty product
        return null;
      }

      if (this.selectedProduct.Items.length === 1) {
        // Only one item (no variations)
        return this.selectedProduct.Items[0];
      }

      // Has variations, return the one matches user selections
      if (
        this.selectedItemDefs == null ||
        Object.keys(this.selectedItemDefs).length === 0 ||
        Object.keys(this.selectedItemDefs).length !==
          Object.keys(this.selectedProduct.ItemDefs).length
      ) {
        // No variations selected or selection not completed yet
        return null;
      }

      // Selection of variations completed, get matched item
      return this.selectedProduct.Items.find((item) => {
        let variationMatch = true;

        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of Object.entries(this.selectedItemDefs)) {
          if (item.ItemDef[key] == null || item.ItemDef[key] !== value) {
            // Not match, stop looping
            variationMatch = false;
            break;
          }
        }

        return variationMatch;
      });
    },
    getTotalPriceWithPersonalDiscount() {
      // Add a personal discount to current price
      const price = this.getTotalPriceWithoutPersonalDiscount();
      return this.getPriceForPersonalDiscount(price);
    },
    getTotalPriceWithoutPersonalDiscount() {
      if (this.isProfUser) {
        return getTotalPriceExclVAT(this.productSelection);
      }
      return getTotalPriceInclVAT(this.productSelection);
    },
    getTotalRetailPriceWithoutPersonalDiscount() {
      if (this.isProfUser) {
        return getTotalRetailPriceExclVAT(this.productSelection);
      }
      return getTotalRetailPriceInclVAT(this.productSelection);
    },
    getTotalRetailPriceWithPersonalDiscount() {
      // For retail price with a personal discount, the current price should be shown
      if (this.applyPersonalDiscount) {
        return this.getTotalPriceWithoutPersonalDiscount();
      }
      return null;
    },
    increaseQuantity() {
      if (this.isWeightedProduct) {
        this.quantity += this.selectedProduct.WeightedInfo.StepSize;
      } else {
        this.quantity += 1;
      }
    },
    decreaseQuantity() {
      if (this.isWeightedProduct) {
        this.quantity -= this.selectedProduct.WeightedInfo.StepSize;
      } else {
        this.quantity -= 1;
      }
    },
    onBookBtnClick(selectedList) {
      if (this.invalidOptionSelection) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('PRODUCT_OPTIONS_ERROR'),
          color: 'warning',
        });
      } else {
        this.$emit('book', {
          ShoppingList: selectedList,
          Product: this.selectedProduct,
          Item: this.selectedItem,
          Quantity: this.quantity,
        });
      }
    },
    onItemDefChange() {
      // For the purpose of triggering reactivity to recompute selectedItem
      const defs = { ...this.selectedItemDefs };
      this.selectedItemDefs = {};
      this.selectedItemDefs = { ...defs };
      this.selectedItem = {};
      this.selectedItem = { ...this.getSelectedItem() };
      this.quantity = getQuantity(
        this.selectedProduct,
        this.selectedItem,
        this.quantity
      );
    },
    onValidateOptionSelection(invalidSelection) {
      this.invalidOptionSelection = invalidSelection;
    },
    getPriceForPersonalDiscount(originalPrice) {
      let price = originalPrice;
      if (this.applyPersonalDiscount) {
        price = utility.getDiscountPrice(originalPrice, this.personalDiscount);
      }
      return price;
    },
    getTotalPriceInclVAT,
    getVATText,
    getSortedItemDefNames,
    getSortedItemDefValues,
    getDisplayQuantity,
    getDisplayUnit,
  },
};
</script>

<style scoped lang="scss">
.add-to-list {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 3;

  @media screen and (min-width: 960px) {
    position: relative;
    z-index: auto;
  }
}
</style>
